import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  schemaData?: object | object[];
  alternateLanguages?: Array<{ lang: string; url: string }>;
  breadcrumbSchema?: object;
  faqSchema?: object;
  localBusinessSchema?: object;
  serviceAreaSchema?: object;
  robots?: string;
}

import {
  getCanonicalUrl,
  normalizeCanonicalUrl,
} from "@/utils/canonical-urls";

export function SEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = "/tigola-logo.png",
  ogUrl,
  schemaData,
  alternateLanguages,
  breadcrumbSchema,
  faqSchema,
  localBusinessSchema,
  serviceAreaSchema,
  robots = "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
}: SEOProps) {
  // Guard against calling hooks outside React context (during HMR or SSR)
  if (typeof window === "undefined") {
    return null;
  }

  useEffect(() => {
    // Helper: normalize page title to recommended length (30–60 chars)
    const normalizeTitle = (text: string) => {
      const MIN = 30;
      const MAX = 60;
      let t = (text || "").replace(/\s+/g, " ").trim();
      if (t.length > MAX) {
        let cut = t.slice(0, MAX);
        const lastSpace = cut.lastIndexOf(" ");
        if (lastSpace > 15) cut = cut.slice(0, lastSpace);
        t = cut.trim();
      } else if (t.length < MIN) {
        const suffix = " | Tigola";
        t = (t ? t + suffix : "Tigola Transport").trim();
        if (t.length > MAX) {
          let cut = t.slice(0, MAX);
          const lastSpace = cut.lastIndexOf(" ");
          if (lastSpace > 15) cut = cut.slice(0, lastSpace);
          t = cut.trim();
        }
      }
      return t;
    };

    // Update document title
    const normalizedTitle = normalizeTitle(title);
    document.title = normalizedTitle;

    // Static British English only
    const currentHl = "en-GB";
    try {
      document.documentElement.setAttribute("lang", "en-GB");
    } catch {}

    // Helper: normalize meta description to recommended length (120–160 chars)
    const normalizeDescription = (
      text: string | undefined,
      fallbackTitle: string,
    ) => {
      const MIN = 120;
      const MAX = 160;
      let content = (text || "").replace(/\s+/g, " ").trim();

      // Trim if too long (preserve whole words)
      if (content.length > MAX) {
        let cut = content.slice(0, MAX);
        const lastSpace = cut.lastIndexOf(" ");
        if (lastSpace > 80) cut = cut.slice(0, lastSpace);
        content = cut.trim();
      }

      // If too short, build a richer description
      if (content.length < MIN) {
        const base =
          "Reliable UK transport with fixed pricing, licensed drivers, live tracking, and 24/7 support from Tigola.";
        content = content ? `${content} ${base}` : `${fallbackTitle} — ${base}`;
        content = content.replace(/\s+/g, " ").trim();
        if (content.length > MAX) {
          let cut = content.slice(0, MAX);
          const lastSpace = cut.lastIndexOf(" ");
          if (lastSpace > 80) cut = cut.slice(0, lastSpace);
          content = cut.trim();
        }
      }
      return content;
    };

    // Update or create meta tags
    const updateMetaTag = (
      name: string,
      content: string,
      property?: boolean,
    ) => {
      const attribute = property ? "property" : "name";
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    // Enhanced basic meta tags
    const normalizedDesc = normalizeDescription(description, title);
    updateMetaTag("description", normalizedDesc);
    updateMetaTag("robots", robots);
    updateMetaTag("author", "Tigola UK Ltd");
    updateMetaTag("viewport", "width=device-width, initial-scale=1.0");
    updateMetaTag("theme-color", "#2563eb");
    updateMetaTag("msapplication-TileColor", "#2563eb");
    updateMetaTag("application-name", "Tigola");

    // Enhanced SEO meta tags
    updateMetaTag("distribution", "global");
    updateMetaTag("rating", "general");
    updateMetaTag("language", "English");
    updateMetaTag("geo.region", "GB");
    updateMetaTag("geo.country", "United Kingdom");
    updateMetaTag("ICBM", "54.7023545,-3.2765753"); // UK geographic center

    // Business-specific meta tags
    updateMetaTag("business:contact_data:street_address", "24 Brambling Road");
    updateMetaTag("business:contact_data:locality", "Leicester");
    updateMetaTag("business:contact_data:region", "East Midlands");
    updateMetaTag("business:contact_data:postal_code", "LE5 3HL");
    updateMetaTag("business:contact_data:country_name", "United Kingdom");
    updateMetaTag("business:contact_data:phone_number", "+44 116 298 2567");
    updateMetaTag("business:contact_data:website", "https://tigola.com");

    // Performance and loading hints
    updateMetaTag("resource-type", "document");
    updateMetaTag("format-detection", "telephone=yes");

    // Enhanced Open Graph tags (with strict length caps)
    const truncateAt = (text: string, max: number) => {
      if (!text) return "";
      if (text.length <= max) return text.trim();
      let cut = text.slice(0, max);
      const lastSpace = cut.lastIndexOf(" ");
      if (lastSpace > 20) cut = cut.slice(0, lastSpace);
      return cut.trim();
    };

    const ogTitleFinal = truncateAt(ogTitle || title, 60);
    const normalizedOgDesc = normalizeDescription(
      ogDescription || description,
      title,
    );
    const ogDescFinal = truncateAt(normalizedOgDesc, 65);

    updateMetaTag("og:title", ogTitleFinal, true);
    updateMetaTag("og:description", ogDescFinal, true);
    updateMetaTag("og:image", ogImage, true);
    updateMetaTag("og:image:width", "1200", true);
    updateMetaTag("og:image:height", "630", true);
    updateMetaTag(
      "og:image:alt",
      "Tigola - Professional UK Transport Service",
      true,
    );
    updateMetaTag("og:type", "website", true);
    updateMetaTag("og:site_name", "Tigola", true);
    try {
      const parts = currentHl.split("-");
      const ogLocale =
        parts.length > 1 ? `${parts[0]}_${parts[1].toUpperCase()}` : parts[0];
      updateMetaTag("og:locale", ogLocale, true);
    } catch {
      updateMetaTag("og:locale", "en_GB", true);
    }
    updateMetaTag("og:country-name", "United Kingdom", true);

    if (ogUrl) {
      updateMetaTag("og:url", ogUrl, true);
    }

    // Enhanced Twitter Card tags
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:site", "@Tigola_UK");
    updateMetaTag("twitter:creator", "@Tigola_UK");
    const twitterTitleFinal = truncateAt(ogTitle || title, 55);
    const twitterDescFinal = truncateAt(normalizedOgDesc, 125);
    updateMetaTag("twitter:title", twitterTitleFinal);
    updateMetaTag("twitter:description", twitterDescFinal);
    updateMetaTag("twitter:image", ogImage);
    updateMetaTag(
      "twitter:image:alt",
      "Tigola - Professional UK Transport Service",
    );
    updateMetaTag("twitter:domain", "tigola.com");

    // Additional social media tags
    updateMetaTag("linkedin:owner", "Tigola UK Ltd");
    updateMetaTag("pinterest:domain", "tigola.com");

    // Local business tags
    updateMetaTag("geo.placename", "United Kingdom");
    updateMetaTag("geo.position", "54.7023545;-3.2765753");

    // Mobile optimization
    updateMetaTag("mobile-web-app-capable", "yes");
    updateMetaTag("apple-mobile-web-app-capable", "yes");
    updateMetaTag("apple-mobile-web-app-status-bar-style", "default");
    updateMetaTag("apple-mobile-web-app-title", "Tigola");
    updateMetaTag("msapplication-starturl", "/");
    updateMetaTag("msapplication-tooltip", "Professional UK Transport Service");

    // Canonical URL - always normalize to the main tigola.com version without query strings.
    let finalCanonicalUrl: string;

    if (canonical) {
      try {
        finalCanonicalUrl = normalizeCanonicalUrl(canonical);
      } catch {
        try {
          const url = new URL(canonical);
          finalCanonicalUrl = getCanonicalUrl(url.pathname);
        } catch {
          finalCanonicalUrl = getCanonicalUrl(window.location.pathname);
        }
      }
    } else {
      finalCanonicalUrl = getCanonicalUrl(window.location.pathname);
    }

    // Set canonical link and mark as page-managed
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", finalCanonicalUrl);
    link.setAttribute("data-managed-by", "page-seo");

    // Add sitemap link for crawlers
    let sitemapLink = document.querySelector('link[rel="sitemap"]');
    if (!sitemapLink) {
      sitemapLink = document.createElement("link");
      sitemapLink.setAttribute("rel", "sitemap");
      sitemapLink.setAttribute("type", "application/xml");
      document.head.appendChild(sitemapLink);
    }
    const sitemapOrigin = finalCanonicalUrl.startsWith("https://tigola.com")
      ? "https://tigola.com"
      : new URL(finalCanonicalUrl).origin;
    sitemapLink.setAttribute("href", `${sitemapOrigin}/sitemap.xml`);

    // FIXED: Hreflang alternates - Use provided alternateLanguages if available
    document
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((el) => el.remove());

    if (alternateLanguages && alternateLanguages.length > 0) {
      const seenAlternateUrls = new Set<string>();

      alternateLanguages.forEach(({ lang, url }) => {
        let normalizedAlternateUrl: string;

        try {
          normalizedAlternateUrl = normalizeCanonicalUrl(url);
        } catch {
          try {
            normalizedAlternateUrl = getCanonicalUrl(new URL(url).pathname);
          } catch {
            return;
          }
        }

        if (seenAlternateUrls.has(normalizedAlternateUrl)) {
          return;
        }

        seenAlternateUrls.add(normalizedAlternateUrl);

        const a = document.createElement("link");
        a.setAttribute("rel", "alternate");
        a.setAttribute("hreflang", lang);
        a.setAttribute("href", normalizedAlternateUrl);
        document.head.appendChild(a);
      });
    }

    // Keep og:url in sync with canonical
    updateMetaTag("og:url", finalCanonicalUrl, true);

    // Remove existing structured data scripts
    document
      .querySelectorAll('script[type="application/ld+json"]')
      .forEach((script) => script.remove());

    // Helper function to validate and sanitize structured data
    const sanitizeSchema = (schema: any): any => {
      if (!schema || typeof schema !== "object") return null;

      // Remove null/undefined values that cause validation errors
      const cleanSchema = JSON.parse(
        JSON.stringify(schema, (key, value) => {
          if (value === null || value === undefined || value === "")
            return undefined;
          return value;
        }),
      );

      // Ensure required @context and @type
      if (!cleanSchema["@context"])
        cleanSchema["@context"] = "https://schema.org";
      if (!cleanSchema["@type"]) return null;

      return cleanSchema;
    };

    // Inferred WebPage schema for all pages
    try {
      const { pathname } = window.location;
      let pageType: string = "WebPage";
      if (pathname === "/") pageType = "WebPage";
      else if (
        ["/airports", "/taxi", "/coverage", "/popular-routes"].includes(
          pathname,
        )
      )
        pageType = "CollectionPage";
      else if (pathname.startsWith("/contact")) pageType = "ContactPage";
      else if (pathname.startsWith("/about")) pageType = "AboutPage";
      else if (pathname.startsWith("/help")) pageType = "FAQPage";

      const webPageSchema = sanitizeSchema({
        "@context": "https://schema.org",
        "@type": pageType,
        name: normalizedTitle,
        url: finalCanonicalUrl,
        inLanguage: "en-GB",
        isPartOf: { "@id": "https://tigola.com/#website" },
        description: normalizedDesc,
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: ogImage.startsWith("http")
            ? ogImage
            : `https://tigola.com${ogImage}`,
          width: 1200,
          height: 630,
        },
      });

      if (webPageSchema) {
        const webPageScript = document.createElement("script");
        webPageScript.setAttribute("type", "application/ld+json");
        webPageScript.textContent = JSON.stringify(webPageSchema);
        document.head.appendChild(webPageScript);
      }
    } catch {}

    // Include WebSite schema site-wide unless already provided via props
    try {
      const providedSchemas = schemaData
        ? Array.isArray(schemaData)
          ? schemaData
          : [schemaData]
        : [];
      const hasWebsite = providedSchemas.some((s: any) => {
        const t = s && (s["@type"] || s.type);
        return t === "WebSite" || (Array.isArray(t) && t.includes("WebSite"));
      });
      if (!hasWebsite) {
        const websiteSchema = sanitizeSchema(generateSchemaData.website());
        if (websiteSchema) {
          const script = document.createElement("script");
          script.setAttribute("type", "application/ld+json");
          script.textContent = JSON.stringify(websiteSchema);
          document.head.appendChild(script);
        }
      }
    } catch {}

    // Main structured data (accept object or array) - with validation
    if (schemaData) {
      const schemas = Array.isArray(schemaData) ? schemaData : [schemaData];
      schemas.forEach((schema) => {
        const cleanSchema = sanitizeSchema(schema);
        if (cleanSchema) {
          const script = document.createElement("script");
          script.setAttribute("type", "application/ld+json");
          script.textContent = JSON.stringify(cleanSchema);
          document.head.appendChild(script);
        }
      });
    }

    // Additional structured data schemas - with validation
    if (breadcrumbSchema) {
      const cleanSchema = sanitizeSchema(breadcrumbSchema);
      if (cleanSchema) {
        const script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.textContent = JSON.stringify(cleanSchema);
        document.head.appendChild(script);
      }
    }

    if (faqSchema) {
      const cleanSchema = sanitizeSchema(faqSchema);
      if (cleanSchema) {
        const script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.textContent = JSON.stringify(cleanSchema);
        document.head.appendChild(script);
      }
    }

    if (localBusinessSchema) {
      const cleanSchema = sanitizeSchema(localBusinessSchema);
      if (cleanSchema) {
        const script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.textContent = JSON.stringify(cleanSchema);
        document.head.appendChild(script);
      }
    }

    if (serviceAreaSchema) {
      const cleanSchema = sanitizeSchema(serviceAreaSchema);
      if (cleanSchema) {
        const script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.textContent = JSON.stringify(cleanSchema);
        document.head.appendChild(script);
      }
    }
  }, [
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    schemaData,
    alternateLanguages,
    breadcrumbSchema,
    faqSchema,
    localBusinessSchema,
    serviceAreaSchema,
  ]);

  return null;
}

import {
  generateValidOrganizationSchema,
  generateCityOrganizationSchema,
  generateAirportOrganizationSchema,
} from "@/utils/enhanced-organization-schema";

// Enhanced helper function to generate schema data for different page types
export const generateSchemaData = {
  organization: (
    organizationName?: string,
    cityName?: string,
    airportCode?: string,
  ) => {
    if (airportCode) {
      return generateAirportOrganizationSchema(
        organizationName || "Airport",
        airportCode,
      );
    }
    if (cityName) {
      return generateCityOrganizationSchema(
        cityName,
        cityName.toLowerCase().replace(/\s+/g, "-"),
      );
    }
    return generateValidOrganizationSchema();
  },

  website: () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://tigola.com/#website",
    url: "https://tigola.com",
    name: "Tigola",
    description:
      "Professional airport transfer and taxi service across the UK with fixed pricing, 24/7 availability, and licensed drivers.",
    publisher: {
      "@id": "https://tigola.com/#organization",
    },
    potentialAction: [
      {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: "https://tigola.com/search?q={search_term_string}",
        },
        "query-input": "required name=search_term_string",
      },
    ],
    inLanguage: "en-GB",
  }),

  breadcrumb: (items: Array<{ name: string; url: string }>) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: {
        "@type": "WebPage",
        "@id": item.url,
      },
    })),
  }),

  faq: (questions: Array<{ question: string; answer: string }>) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer,
      },
    })),
  }),

  airport: (
    airportName: string,
    airportCode: string,
    city: string,
    description: string,
  ) => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://tigola.com/airports/${airportCode.toLowerCase()}/#business`,
    name: `Tigola Airport Transfer - ${airportName}`,
    description: `Professional airport transfer service to ${airportName} (${airportCode}). ${description}`,
    serviceType: [
      "Airport Transfer Service",
      "Private Hire",
      "Executive Transport",
    ],
    areaServed: [
      {
        "@type": "City",
        name: city,
      },
      {
        "@type": "Airport",
        name: airportName,
        iataCode: airportCode,
      },
    ],
    url: `https://tigola.com/airports/${airportCode.toLowerCase()}`,
    telephone: "+44 116 298 2567",
    email: "contact-us@tigola.com",
    priceRange: "£15-£70",
    paymentAccepted: [
      "Cash",
      "Credit Card",
      "Debit Card",
      "PayPal",
      "Apple Pay",
      "Google Pay",
    ],
    currenciesAccepted: "GBP",
    openingHours: ["Mo-Su 00:00-23:59"],
    hasMap: `https://maps.google.com/?q=${encodeURIComponent(airportName + " Airport")}`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      bestRating: "5",
      worstRating: "1",
      ratingCount: "2847",
      reviewCount: "2847",
    },
    review: [
      {
        "@type": "Review",
        reviewRating: {
          "@type": "Rating",
          ratingValue: "5",
          bestRating: "5",
        },
        author: {
          "@type": "Person",
          name: "Sarah Johnson",
        },
        reviewBody: `Excellent ${airportName} airport transfer service. Driver was punctual and professional.`,
        datePublished: "2024-01-15",
      },
    ],
    potentialAction: {
      "@type": "ReserveAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `https://tigola.com/airports/${airportCode.toLowerCase()}`,
      },
      result: {
        "@type": "Reservation",
        name: "Airport Transfer Booking",
      },
    },
  }),

  service: (serviceName: string, description: string, priceRange?: string) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Tigola ${serviceName}`,
    description: description,
    provider: {
      "@id": "https://tigola.com/#organization",
    },
    areaServed: {
      "@type": "Country",
      name: "United Kingdom",
    },
    serviceType: serviceName,
    hoursAvailable: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: "00:00",
      closes: "23:59",
    },
    offers: {
      "@type": "Offer",
      priceRange: priceRange || "£6-£50",
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
      validFrom: "2024-01-01",
      validThrough: "2024-12-31",
    },
  }),

  localBusiness: (
    locationName: string,
    region: string,
    description: string,
  ) => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://tigola.com/cities/${locationName.toLowerCase().replace(/\s+/g, "-")}/#business`,
    name: `Tigola Taxi Service - ${locationName}`,
    description: `Professional taxi and transport service in ${locationName}, ${region}. ${description}`,
    serviceType: [
      "Taxi Service",
      "Private Hire",
      "Airport Transfer",
      "Executive Transport",
    ],
    areaServed: [
      {
        "@type": "Place",
        name: locationName,
        addressRegion: region,
        addressCountry: "United Kingdom",
      },
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: locationName,
      addressRegion: region,
      addressCountry: "GB",
    },
    url: `https://tigola.com/taxi/${locationName.toLowerCase().replace(/\s+/g, "-")}`,
    telephone: "+44 116 298 2567",
    email: "contact-us@tigola.com",
    priceRange: "£6-£50",
    paymentAccepted: [
      "Cash",
      "Credit Card",
      "Debit Card",
      "PayPal",
      "Apple Pay",
      "Google Pay",
    ],
    currenciesAccepted: "GBP",
    openingHours: ["Mo-Su 00:00-23:59"],
    hasMap: `https://maps.google.com/?q=${encodeURIComponent(locationName + ", " + region)}`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      bestRating: "5",
      worstRating: "1",
      ratingCount: "1247",
      reviewCount: "1247",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Transport Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Local Taxi Rides",
            description: `Local taxi transportation within ${locationName}`,
          },
          priceRange: "£6-£15",
          availability: "https://schema.org/InStock",
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Airport Transfers",
            description: `Airport transfer service from ${locationName}`,
          },
          priceRange: "£25-£70",
          availability: "https://schema.org/InStock",
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Business Transport",
            description: `Professional business transport in ${locationName}`,
          },
          priceRange: "£15-£50",
          availability: "https://schema.org/InStock",
        },
      ],
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "54.7023545",
      longitude: "-3.2765753",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+44 116 298 2567",
      contactType: "customer service",
      availableLanguage: "English",
      hoursAvailable: "Mo-Su 00:00-23:59",
    },
  }),

  serviceArea: (areas: string[]) => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://tigola.com/#servicearea",
    name: "Tigola Service Areas",
    serviceArea: areas.map((area) => ({
      "@type": "Place",
      name: area,
      addressCountry: "GB",
    })),
  }),
};

// SEO utility functions
export const seoUtils = {
  generateKeywords: (
    primary: string[],
    secondary: string[],
    location?: string,
  ) => {
    const locationKeywords = location
      ? [
          `${location} taxi`,
          `${location} airport transfer`,
          `${location} private hire`,
          `${location} transport`,
          `taxi service ${location}`,
          `rides ${location}`,
          `transport service ${location}`,
        ]
      : [];

    return [...primary, ...secondary, ...locationKeywords].join(", ");
  },

  generateDescription: (
    service: string,
    location: string,
    features: string[],
  ) => {
    return `Professional ${service} in ${location}. ${features.join(", ")}. Book with Tigola for reliable, safe transport. Available 24/7 with fixed pricing.`;
  },

  generateTitle: (
    service: string,
    location: string,
    suffix: string = "Tigola UK",
  ) => {
    const baseTitle = `${service} ${location}`;
    const fullTitle = `${baseTitle} | ${suffix}`;

    // Keep under 60 characters for SEO
    if (fullTitle.length > 60) {
      return baseTitle.length <= 50 ? `${baseTitle} | Tigola` : baseTitle;
    }

    return fullTitle;
  },

  // Generate unique meta descriptions to avoid duplicates
  generateUniqueDescription: (
    location: string,
    region: string,
    serviceType: string = "taxi service",
    uniqueFeatures?: string[],
  ) => {
    const features = uniqueFeatures || [
      "fixed pricing",
      "professional drivers",
      "24/7 availability",
      "real-time tracking",
    ];

    return `Professional ${serviceType} in ${location}, ${region}. ${features.join(", ")}. Book online for reliable transport with no surge pricing. Licensed drivers serving ${location} and surrounding areas.`;
  },
};
